import { useAtom } from "jotai";
import { useUpdateAtom } from "jotai/utils";
import { useCallback, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { useSendAnalyticsEvent } from "../../analytics/events";
import createBlock from "../../blocks/createBlock";
import {
  spaceMenuOpenAtom,
  blocksRefAtom,
  blockYouJustCreatedAtom,
  currentUserAtom,
  isInAuthFlowAtom,
  selectionAtom,
} from "../../jotai/atoms";
import { useBlocks } from "../../liveblocks/maps";
import { usePresence } from "../../liveblocks/presences";
import {
  AnyBlock,
  BlockType,
  Point2D,
  Presence,
  Selection,
  Size,
} from "../../types";
import { canvasPos } from "../../utils/canvasPos";
import { equalKeys } from "../../utils/equalKeys";
import { gridToPixelPosition, gridToPixelSize } from "../../utils/gridPosition";
import withSelectModifierKey from "../../utils/withSelectModifierKey";
import useDragMove from "./useDragMove";
import { calcPreviewRect, deriveCreateType, isOverlapping } from "./utils";

export default function usePreview() {
  const somethingWasFocused = useRef(false);

  const [previews, updatePresence] =
    usePresence<Presence["preview"]>("preview");
  const [currentUser] = useAtom(currentUserAtom);
  const [blocksRef] = useAtom(blocksRefAtom);
  const [menuOpen, setMenuOpen] = useAtom(spaceMenuOpenAtom);

  const [createNewBlock, removeOtherCreateBlock] = useCreateNewBlock();
  const [selection, setSelection] = useAtom(selectionAtom);
  const [isInAuthFlow] = useAtom(isInAuthFlowAtom);
  const onMouseDownDragMove = useDragMove();

  const preview = Object.entries(previews).find(
    ([userId]) => userId === currentUser?.id
  )?.[1];

  const stateRef = useRef({
    mousedown: null as MouseEvent | null,
    mouseover: null as MouseEvent | null,
    mousedownBlock: undefined as AnyBlock | undefined,
    mousedownSelected: null as AnyBlock["id"] | null,
    selectionBeforeDrag: {} as Selection,

    preview,
    createNewBlock,
    removeOtherCreateBlock,
    selection,
  });

  stateRef.current = {
    ...stateRef.current,
    preview,
    createNewBlock,
    removeOtherCreateBlock,
    selection,
  };

  /*
   * update user's preview base on the current drag state (stateRef)
   */
  const updateDragPreview = useCallback(() => {
    const { mousedownBlock, mousedown, mouseover, selection } =
      stateRef.current;
    if (!mousedown || mousedownBlock) {
      updatePresence({ preview: null });
      return;
    }

    const start = mousedown;
    const end = mouseover || mousedown;

    const { position, size } = calcPreviewRect(start, end);
    const preview = {
      position,
      size,
      select: Object.keys(selection).length > 0,
    };
    updatePresence({ preview });
    return preview;
  }, [updatePresence, stateRef]);

  const onMouseDown = useCallback(
    (event: MouseEvent) => {
      flushSync(() => {
        const { target } = event;
        const { selection } = stateRef.current;

        somethingWasFocused.current =
          Object.keys(selection).length > 0 ||
          (document.hasFocus() &&
            document.activeElement !== null &&
            document.activeElement !== document.body &&
            document.activeElement !== document.documentElement);

        if (target instanceof Element && target.closest("button, input")) {
          return;
        }
        // Make sure click is targeted at canvas
        if (target instanceof Element && !target?.closest(".main-canvas")) {
          return;
        }

        stateRef.current.mousedown = event;
        const mousedownBlock = mouseTarget(
          event,
          blocksRef.current,
          selectionFilter
        );
        stateRef.current.mousedownBlock = mousedownBlock;

        updateDragPreview();

        // selection
        const isMulti = withSelectModifierKey(event);
        let selectionNext: Selection = selection;

        if (mousedownBlock && isMulti) {
          if (mousedownBlock.id in selection) {
            // we don't want to de-select on mousedown because the user may want
            // to drag to move. Also de-select typically occur on mouseup in other
            // system such as macOS.
          } else {
            selectionNext = {
              ...selection,
              [mousedownBlock.id]: Date.now(),
            };
            stateRef.current.mousedownSelected = mousedownBlock.id;
          }
        }

        if (mousedownBlock && !isMulti) {
          if (!(mousedownBlock.id in selection)) {
            selectionNext = { [mousedownBlock.id]: Date.now() };
            stateRef.current.mousedownSelected = mousedownBlock.id;
          }
        }

        if (!mousedownBlock && isMulti) {
          selectionNext = selection;
        }

        if (!mousedownBlock && !isMulti) {
          selectionNext = {};
        }
        setSelection(selectionNext);
        stateRef.current.selectionBeforeDrag = selectionNext;
      });
      // Flush sync to opt out of batching
      // https://reactjs.org/blog/2022/03/08/react-18-upgrade-guide.html#automatic-batching
      flushSync(() => {
        onMouseDownDragMove(event);
      });
    },
    [blocksRef, onMouseDownDragMove, setSelection, updateDragPreview]
  );

  const onMouseOver = useCallback(
    (mouseover: MouseEvent) => {
      const { mousedown, mousedownBlock, selectionBeforeDrag, selection } =
        stateRef.current;

      if (!mousedown) return;

      stateRef.current.mouseover = mouseover;

      if (!mousedownBlock) {
        const preview = updateDragPreview();

        // determine block selection
        if (preview) {
          const blocksCovered = Object.values(blocksRef.current)
            .filter((b) => {
              if (b.locked) return false;
              // Exlcude svg because we only want to select it on path click
              // For discussion see https://linear.app/sproutplace/issue/SPR-396#comment-b442cffd
              if (b.type === "svg") return false;
              return isOverlapping(preview, b);
            })
            .map((b) => b.id);

          const deselect = blocksCovered.filter(
            (id) => id in selectionBeforeDrag
          );

          const now = Date.now();
          let selectionNext: Selection = blocksCovered.reduce(
            (sel, id) => ({ ...sel, [id]: now }),
            {}
          );
          selectionNext = { ...selectionNext, ...selectionBeforeDrag };

          for (const id in selectionNext) {
            if (id in deselect) {
              delete selectionNext[id];
            }
          }

          // important optimization. we don't want to make
          // unnecessary selection update on every mousemove
          if (!equalKeys(selection, selectionNext)) {
            setSelection(selectionNext);
          }
        }
      }
    },
    [blocksRef, setSelection, updateDragPreview]
  );

  const onMouseUp = useCallback(
    (event: MouseEvent) => {
      const {
        mousedownBlock,
        mousedown,
        mouseover,
        preview,
        createNewBlock,
        removeOtherCreateBlock,
        selection,
        mousedownSelected,
      } = stateRef.current;

      if (mousedown && !mousedownBlock && preview) {
        const { position, size } = preview;

        if (
          size.height + size.width <= 2 &&
          !menuOpen &&
          !somethingWasFocused.current &&
          event.button === 0
        ) {
          setMenuOpen(canvasPos(event));
        }
        const type = deriveCreateType(size);

        if (type === "no-block") {
          removeOtherCreateBlock();
        } else {
          if (Object.keys(selection).length === 0) {
            const blockType = type === "text-block" ? "text/json" : "menu";
            createNewBlock(blockType, position, size);
          }
        }
      }

      // selection
      const target = mouseTarget(event, blocksRef.current, selectionFilter);
      if (
        target &&
        target.id in selection &&
        target.id !== mousedownSelected &&
        !mouseover &&
        event.button === 0
      ) {
        delete selection[target.id];
        setSelection(selection);
      }

      if (mousedownSelected) {
        stateRef.current.mousedownSelected = null;
      }

      // cleanup
      if (mousedown) {
        stateRef.current.mousedown = null;
        updateDragPreview();
      }

      if (mouseover) {
        stateRef.current.mouseover = null;
      }
    },
    [blocksRef, menuOpen, setMenuOpen, setSelection, updateDragPreview]
  );

  useEffect(() => {
    if (isInAuthFlow) {
      return;
    }

    // we attach mouse listener on root instead of canvas
    // as we want to capture mouse event on canvas's neighbor
    // elements (PreviewList)
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseOver);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseOver);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [isInAuthFlow, onMouseDown, onMouseOver, onMouseUp]);

  return null;
}

function useCreateNewBlock() {
  const { upsertBlock, deleteBlock } = useBlocks();
  const [blocksRef] = useAtom(blocksRefAtom);
  const setBlockYouJustCreated = useUpdateAtom(blockYouJustCreatedAtom);
  const [currentUser] = useAtom(currentUserAtom);
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  function removeOtherCreateBlock() {
    const generic = Object.values(blocksRef.current)
      .filter((b) => b.type === "menu" && b.madeBy === currentUser?.id)
      .map((b) => b.id);

    deleteBlock(...generic);
  }

  function createNewBlock(blockType: BlockType, position: Point2D, size: Size) {
    const block = createBlock(blockType, position, size);

    if (block) {
      sendAnalyticsEvent({ name: "blockCreated", type: block.type });
      setBlockYouJustCreated(block.id);
      removeOtherCreateBlock();
      upsertBlock(block);
    }
  }

  return [createNewBlock, removeOtherCreateBlock] as [
    typeof createNewBlock,
    typeof removeOtherCreateBlock
  ];
}

/**
 * as intented product behavior, we purposely omit locked block and
 * SVG drawing block from mouse drag selection
 */
function selectionFilter(block: AnyBlock, event: MouseEvent) {
  if (block.type === "svg") {
    const { target } = event;
    return target instanceof Element && target.nodeName === "path";
  }
  if (block.locked) return false;
  return true;
}

export function mouseTarget(
  event: MouseEvent,
  blocks: Record<AnyBlock["id"], AnyBlock>,
  filter?: (block: AnyBlock, event: MouseEvent) => boolean
) {
  return Object.values(blocks)
    .filter((b) => (filter ? filter(b, event) : true))
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .find((block) => {
      // Filter out incomplete blocks
      if (!block.size || !block.position) return false;
      const pos = gridToPixelPosition(block.position);
      const size = gridToPixelSize(block.size);
      const { x, y } = canvasPos(event);

      // prettier-ignore
      return (
        (pos.x < x && x < pos.x + size.width) &&
        (pos.y < y && y < pos.y + size.height)
      );
    });
}
